import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useMetaData } from "../../api/useMetaDataApi";
import { usePayerLandscapeDetail, usePayerLandscapeDetailDetailExport } from "../../api/usePayerLandscapeDetailApi";
import Table from "../../components/Table/Table";
import { Column } from "../../components/Table/types";
import { useClientConfiguration } from "../../data/global/useProductConfiguration";
import { usePayerLandscapeDispatch, usePayerLandscapeState } from "../../data/payerLandscape/hooks";
import { PlanFeed, SortDirection } from "../../generate";
import useGetRole, { FRM_ROLE } from "../../hooks/useGetRole";
import PrintWinLegend from "./PrintWinLegend";
import useDetailColumns from "./useDetailColumns";
import { usePayerLandscapeColumnVisibility } from "./usePayerLandscapeColumnVisibility";

// Define the sort type based on what's in your actual code
interface Sort {
  field: string;
  direction: SortDirection;
}

const frmExcluded = ["mkt_trx", "prod_trx", "mkt_share", "share_delta"];

// Define props directly in the component declaration
const PayerLandscapeDetailTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    detailSort,
    detailFilters,
    sharedFilters: { timePeriod },
  } = usePayerLandscapeState();
  const { updateDetailSort } = usePayerLandscapeDispatch();
  const { client: selectedClient } = useParams() as { client: string };
  const { data: role } = useGetRole(selectedClient);
  const isFRM = String(role) === FRM_ROLE;
  const clientConfig = useClientConfiguration(selectedClient);
  const showNrxCompTrxToggle = (clientConfig?.payerLandscape?.showNrxCompTrxToggle && !isFRM) ?? false;
  const useDefault = !i18n.exists(`payerLandscape.coverageStatus.${selectedClient}`);

  // Memoize the detail columns to prevent unnecessary recalculations
  const { detailColumns } = useDetailColumns(selectedClient, useDefault ? "default" : selectedClient);

  const { visibleColumns } = usePayerLandscapeColumnVisibility(selectedClient, detailFilters.indication, String(role));

  const [rightColumnMetric, setRightColumnMetric] = useState<"prod_nrx" | "comp_prod_trx">("prod_nrx");

  const competitorProduct = useMemo(() => {
    const products = (clientConfig?.products || []).filter((p) => p);
    // If there is more than one product, try to find the "other" product;
    // otherwise, fall back to a generic competitor label.
    if (products.length > 1) {
      const competitor = products.find((p) => p !== detailFilters.product);
      return competitor || "Competitor";
    }
    return "Competitor";
  }, [clientConfig?.products, detailFilters.product]);

  // Memoize the filtered columns for FRM role
  const roleColumns = useMemo(() => {
    if (!isFRM) {
      return detailColumns;
    }

    // For FRM users, filter out excluded columns
    return detailColumns.filter((column) => {
      if (frmExcluded.includes(column.dataKey)) {
        return false;
      }

      // For BRAFTOVI client, also exclude comp_prod_trx
      if (selectedClient === "BRAFTOVI" && column.dataKey === "comp_prod_trx") {
        return false;
      }

      return true;
    });
  }, [isFRM, detailColumns, selectedClient]);

  const sortedColumns: Column[] = useMemo(() => {
    if (!visibleColumns) return [];
    const hasToggle = visibleColumns.includes("prod_nrx" as keyof PlanFeed);
    const inactive = hasToggle ? (rightColumnMetric === "prod_nrx" ? "comp_prod_trx" : "prod_nrx") : null;
    return roleColumns
      .filter(({ dataKey }) => visibleColumns?.includes(dataKey as keyof PlanFeed) && dataKey !== inactive)
      .sort((a, b) => {
        // Sort based on the index in columnsToUse array
        return (
          visibleColumns.indexOf(a.dataKey as keyof PlanFeed) - visibleColumns.indexOf(b.dataKey as keyof PlanFeed)
        );
      });
  }, [roleColumns, visibleColumns, rightColumnMetric]);

  const columnNames = useMemo(
    () => sortedColumns.map((column) => t(column.label, column.translationParams)),
    [sortedColumns, t],
  );

  const columnKeys = useMemo(() => sortedColumns.map((column) => column.dataKey), [sortedColumns]);

  // Memoize API calls to prevent unnecessary re-fetching
  const detailQueryParams = useMemo(
    () => ({
      client: selectedClient,
    }),
    [selectedClient],
  );

  const detailQuery = usePayerLandscapeDetail(detailQueryParams.client);
  const exportTableParams = useMemo(
    () => ({
      client: selectedClient,
      columnKeys,
      columnNames,
    }),
    [selectedClient, columnKeys, columnNames],
  );

  const exportTable = usePayerLandscapeDetailDetailExport(
    exportTableParams.client,
    exportTableParams.columnKeys,
    exportTableParams.columnNames,
  );

  const metaDataParams = useMemo(
    () => ({
      client: selectedClient,
      timePeriod: timePeriod || "",
    }),
    [selectedClient, timePeriod],
  );

  const { data: metaData } = useMetaData(metaDataParams.client, metaDataParams.timePeriod);

  // Memoize the sort handler with explicit type
  const handleSort = useCallback(
    (sort: Sort) => {
      updateDetailSort(sort);
    },
    [updateDetailSort],
  );

  useEffect(() => {
    updateDetailSort({ field: "mkt_trx", direction: SortDirection.Descending });
  }, [updateDetailSort]);

  // Generate footnote content only once
  const footnoteContent = useMemo(() => {
    const baseKey = `payerLandscape.detailedViewFootnote.${selectedClient}`;
    const defaultKey = `payerLandscape.detailedViewFootnote.default`;

    if (role) {
      const roleSpecificKey = `${baseKey}_${role}`;

      if (i18n.exists(roleSpecificKey)) {
        return t(roleSpecificKey);
      }
    }

    return t([baseKey, defaultKey]);
  }, [t, selectedClient, role, i18n]);

  // Only update the key when indication changes to prevent unnecessary re-renders
  const tableKey = useMemo(() => `detailTable-${detailFilters.indication}`, [detailFilters.indication]);

  const rightColumnToggle = showNrxCompTrxToggle ? (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={rightColumnMetric}
      onChange={(_, value) => {
        if (value) setRightColumnMetric(value);
      }}
      aria-label="right column metric"
      sx={{ "& .MuiToggleButton-root": { padding: "2px 8px", fontSize: "0.7rem", textTransform: "none" } }}
    >
      <ToggleButton value="prod_nrx" aria-label="product NRx">
        {detailFilters.product} NRx
      </ToggleButton>
      {competitorProduct && (
        <ToggleButton value="comp_prod_trx" aria-label="competitor TRx">
          {competitorProduct} TRx
        </ToggleButton>
      )}
    </ToggleButtonGroup>
  ) : undefined;

  return (
    <Table
      key={tableKey}
      columns={sortedColumns}
      query={detailQuery}
      sort={detailSort}
      stickyMargin={54}
      handleSort={handleSort}
      export={exportTable}
      legend={<PrintWinLegend />}
      footnote={footnoteContent}
      boldFootnote={selectedClient === "CIBINQO"}
      metaData={metaData}
      role={String(role)}
      lastColumnOverlay={rightColumnToggle}
    />
  );
};

export default React.memo(PayerLandscapeDetailTab);
